import type { MediaItem } from "@/components/media-preview-modal/mediaPreviewModal.types";

import { FILE_ICONS } from "@/components/file-uploader/file-icons";
import { getExtensionFromFilename } from "@/utils/getExtensionFromMime";

import type { ManagedFile } from "./types";

const blobUrlCache = new Map<string, string>();

export function convertManagedFilesToMediaItems(
  files: ManagedFile[],
): MediaItem[] {
  return files
    .filter((f) => f.status === "success")
    .map((f) => {
      const name = getFileName(f);
      let url = f.url;

      if (!url && f.file) {
        const cacheKey = `${f.id}-${f.file.name}-${f.file.size}`;

        if (!blobUrlCache.has(cacheKey)) {
          blobUrlCache.set(cacheKey, URL.createObjectURL(f.file));
        }
        url = blobUrlCache.get(cacheKey);
      }

      return {
        extension: getExtensionFromFilename(name),
        id: f.id,
        name,
        url: url ?? "",
      };
    })
    .filter((item) => item.url !== "");
}

export function getFileName(file: ManagedFile): string {
  return file.file?.name ?? file.name ?? "Unknown file";
}

export function getFileUrl(file: ManagedFile): string | undefined {
  if (file.url) return file.url;
  if (file.file) return URL.createObjectURL(file.file);

  return undefined;
}

export function getFileSize(file: ManagedFile): number | undefined {
  return file.file?.size;
}

export function formatFileSize(bytes: number | undefined): string {
  if (bytes === undefined || bytes === 0) return "";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function getFileIcon(fileName: string) {
  const extension = fileName.split(".").pop()?.toLowerCase();

  if (extension && extension in FILE_ICONS) {
    return FILE_ICONS[extension as keyof typeof FILE_ICONS];
  }

  return FILE_ICONS.fallback;
}

export function getFileExtension(file: ManagedFile): null | string {
  const fileName = file.file?.name || file.name;

  if (!fileName) return null;

  const extension = fileName.split(".").pop()?.toLowerCase();

  return extension || null;
}
